import {
    Component,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ViewEncapsulation,
    ViewChild,
    ElementRef,
    HostListener
} from "@angular/core";
import {HttpErrorResponse} from "@angular/common/http";
import {IrbEditor} from "@irb/irb-editors/irb-editors.base";
import {IModelDetail, IPortfolioDetail, IPortfolioDetailSaveResponse} from "@irb/irb-editors/irb-editors.model";
import {IEvent, IrbAppEvents, IrbClientsEvents, IrbEditorsEvents,IrbModelsEvents} from "@irb-common/events";
import {IAccount} from '@irb/irb-clients/irb-clients.model';
import {ComAccountsComponent} from "./com-accounts/com-accounts.component";
import {IrbEditorsService} from '@irb/irb-editors/irb-editors.service';
import { IAlertDialogData } from "@irb/app.model";
import {AnalyticsService} from "@irb-common/analytics/analytics.service";
import {forkJoin, Observable, of, ReplaySubject} from 'rxjs';
import {IrbSettingsService} from "@irb/irb-settings/irb-settings.service";
import {IGlobalSettings} from "@irb/irb-settings/irb-settings.model";
import {faChevronRight} from "@fortawesome/free-solid-svg-icons";

@Component({ standalone: false,
    selector: "irb-editor-portfolio",
    templateUrl: "./editor-portfolio.component.html",
    styleUrls: ["./editor-portfolio.component.scss", "../../irb-common/styles/dialog.scss", "../editor-sidebar/editor-sidebar.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None,
    providers: [IrbEditorsService]
})
export class EditorPortfolioComponent extends IrbEditor {
    @ViewChild("body") body: ElementRef;
    @ViewChild("assignedAccounts") _assignedAccounts: ComAccountsComponent;

    defaultExpandedPanels: Array<string> = ["ACCOUNTS","SETTINGS","CUSTOM_LABELS","SECURITY_RESTRICTIONS", "EQUIVALENTS_ALTERNATIVES","GAINS_MANAGEMENT","SCHEDULED_REBALANCES_LIST","PORTFOLIO_NOTES","AUTOMATED_REBALANCING"];

    accountsTabInit: boolean;
    restrictionsTabInit: boolean;
    gainsTabInit: boolean;
    notesTabInit: boolean;
    scheduledRebalancesTabInit: boolean;
    _collapseForOpenTriggers: boolean;
    showInLineeditFlag: boolean;
    tooltipMessage: any;
    _isUnAuthorized: boolean = false;
    isUiTest = false;
    updateStatusCount=0;
    timeout=0;
    globalSettings: IGlobalSettings;
    isGlobalSettings: boolean;
    isAutoRebalanceEnabled: boolean;
    isTLHSetInGlobal: boolean;
    //private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
    portfolioId: any;
    automatedRebalanceInit: boolean;
    equivalentsAlternativesTabInit: boolean;
    isPortfEqvEnabled: boolean;
    selectedModel: IModelDetail = {} as IModelDetail;
    reloadAfterModelClosed: boolean = false;
    isGainsManagementValid: boolean = true;
    isSecurityRestrictionsValid: boolean = true;

    constructor(private cdr: ChangeDetectorRef,
                public localEditorsService: IrbEditorsService,
                public ats: AnalyticsService,
                public iss: IrbSettingsService
                ) {
        super();
        this.identifier = "portfolioId";
    }

    get(event: IEvent) {
        super.get(event);

        this.isPortfEqvEnabled = this.as.getItem("IRB_APP_CONFIG")?.featureFlags.isPortfEqvEnabled || false;

        this.iss.getGlobalSettings().subscribe((globalSettings: any) => {
            this.globalSettings = globalSettings;
            this.isGlobalSettings = true;
            this.isAutoRebalanceEnabled = this.globalSettings?.isAutoRebalanceEnabled;
            this.isTLHSetInGlobal = globalSettings.isTlhLotLevel || globalSettings.isTlhPositionLevel;
        });

        this.ats.trackEditPortfolioEvent();
        this.initExpansionPanels(event);
        this.portfolioId = event.data.data.portfolioId;
        let buildedData: IPortfolioDetail;
        const getPortfolioAssignedAccounts = this.ies.getPortfolioAssignedAccounts(this.portfolioId);
        const getPortfolioRestrictions = this.ies.getPortfolioRestrictions(this.portfolioId);
        const getPortfolioScheduledRebalances = this.ies.getPortfolioScheduledRebalances(this.portfolioId);
        const getPortfolioNotes = this.ies.getPortfolioNotes(this.portfolioId);
        const getPortfolioAutoScheduler = this.ies.getPortfolioAutoScheduler(this.portfolioId);
        const getPortfolioEquivalents = this.isPortfEqvEnabled ? this.ies.getPortfolioEquivalents(this.portfolioId) : this.getEmptyResponse();

         this.ies.getOnlyPortfolioDetails(this.portfolioId).subscribe((pfDetails: any) => {
            if (pfDetails.responseStatus === "SUCCESS") {
                buildedData = pfDetails.responseData;
                forkJoin([
                    getPortfolioAssignedAccounts,
                    getPortfolioRestrictions,
                    getPortfolioScheduledRebalances,
                    getPortfolioNotes,
                    getPortfolioAutoScheduler,
                    getPortfolioEquivalents
                  ]
                ).subscribe(([portfolioAssignedAccounts, portfolioRestrictions, portfolioScheduledRebalances, portfolioNotes, portfolioAutoScheduler, portfolioEquivalent /*, portfolioAutoRebalResultList*/]) => {
                    const accountsSet: any = portfolioAssignedAccounts;
                    if (accountsSet.responseStatus === "SUCCESS" && accountsSet.responseData)
                        buildedData.accountsSet = accountsSet.responseData;

                    const pfRestrictions: any = portfolioRestrictions;
                    if (pfRestrictions.responseStatus === "SUCCESS" && pfRestrictions.responseData)
                        buildedData.portfolioRestrictions = pfRestrictions.responseData;

                    const analysisSet: any = portfolioScheduledRebalances;
                    if (analysisSet.responseStatus === "SUCCESS" && analysisSet.responseData)
                        buildedData.analysisSet = analysisSet.responseData;

                    const listOfNotes: any = portfolioNotes;
                    if (listOfNotes.responseStatus === "SUCCESS" && listOfNotes.responseData)
                        buildedData.listOfNotes = listOfNotes.responseData;

                    const pfEquivalent: any = portfolioEquivalent;
                    if (pfEquivalent.data && pfEquivalent.data.irebalResponse) {
                        buildedData.portfolioEquivalents = pfEquivalent.data.irebalResponse.responseData;
                    }

                    const autoScheduler: any = portfolioAutoScheduler;
                    if (autoScheduler.responseStatus === "SUCCESS") {
                        if (!autoScheduler.responseData) {
                            buildedData.isAutoRebalEnabled = false;
                        }
                        if (!buildedData.isAutoRebalEnabled) {
                            buildedData.autoRebalRequestedBy = null;
                            buildedData.autoRebalStartDate = null;
                            buildedData.autoRebalType = null;
                        }
                        if (autoScheduler.responseData) {
                            //console.log('autoScheduler.responseData', autoScheduler.responseData);
                            if (!autoScheduler.responseData.autoSchedulerId) { autoScheduler.responseData.autoSchedulerId = null;}
                            if (!autoScheduler.responseData.version ) { autoScheduler.responseData.version = 0;}
                            if (!autoScheduler.responseData.scheduleType) { autoScheduler.responseData.scheduleType = 'Recurring';}
                            if (!autoScheduler.responseData.monthlyRecurrenceType) { autoScheduler.responseData.monthlyRecurrenceType = null;}
                            if (!autoScheduler.responseData.endDateType) { autoScheduler.responseData.endDateType = 'No End Date';}
                            if (!autoScheduler.responseData.dayName) { autoScheduler.responseData.dayName = null;}
                            if (!autoScheduler.responseData.endByDate) { autoScheduler.responseData.endByDate = null;}
                            //if (!autoScheduler.responseData.endAfterNTimes) { autoScheduler.responseData.endAfterNTimes = 1;}

                            if (!autoScheduler.responseData.recurringFreqType) {autoScheduler.responseData.recurringFreqType = 'Daily';}
							if (autoScheduler.responseData.isActive === null) {autoScheduler.responseData.isActive = true;}
							if (typeof autoScheduler.responseData.isActive === 'undefined') {autoScheduler.responseData.isActive = true;}
                            if (!autoScheduler.responseData.isActive) {autoScheduler.responseData.isActive = false;}
                            if (autoScheduler.responseData.recurringFreqType === 'Daily' && !autoScheduler.responseData.everyNDays) {autoScheduler.responseData.everyNDays = 1;}
                            if (autoScheduler.responseData.recurringFreqType === 'Weekly' && !autoScheduler.responseData.everyNWeeks) {autoScheduler.responseData.everyNWeeks = 1;}
                            if (autoScheduler.responseData.recurringFreqType === 'Monthly' && !autoScheduler.responseData.everyNMonths) {autoScheduler.responseData.everyNMonths = 1;}
                            if (autoScheduler.responseData.recurringFreqType === 'Monthly' && !autoScheduler.responseData.monthlyRecurrenceType) {autoScheduler.responseData.monthlyRecurrenceType = 'a specific date';}
                            if (autoScheduler.responseData.recurringFreqType === 'Monthly' && !autoScheduler.responseData.dateNumber) {autoScheduler.responseData.dateNumber = 1;}
                            buildedData.autoScheduler = autoScheduler.responseData;
                        }
                    }
                    this.load(buildedData);
                }, () => {
                    // EF after discussing with Jeff if failed we keep slide-out gray
                });
            }
        });
    }

    getEmptyResponse() {
        const response: any = {
            "data": {
                "irebalResponse": {
                    "responseData": null
                }
            }
        }
        return of(response);
    }

    load(data: IPortfolioDetail) {
        super.load(data);
        this.setPortfolioAuthorization();
        this.tooltipMessage = "";
        this.showInLineeditFlag = false;
        this.cdr.detectChanges();
        setTimeout(() => this.onResize());
    }

    initExpansionPanels(event: IEvent) {
        this.accountsTabInit = event.data.acountsinit || false;
        this.restrictionsTabInit = event.data.restrictionsinit || false;
        this.gainsTabInit = event.data.gainsinit || false;
        this.notesTabInit = event.data.notesinit || false;
        this.scheduledRebalancesTabInit = event.data.scheduledRebalancesInit || false;
        this.automatedRebalanceInit = event.data.automatedRebalanceInit || false;
        this.equivalentsAlternativesTabInit = event.data.equivalentsAlternativesTabInit || false;
        this._collapseForOpenTriggers = (this.accountsTabInit 
            || this.restrictionsTabInit 
            || this.gainsTabInit 
            || this.notesTabInit 
            || this.scheduledRebalancesTabInit 
            || this.automatedRebalanceInit 
            || this.equivalentsAlternativesTabInit) ? true : false;
        let dep = this.defaultExpandedPanels;
        let sessionOverridesDefault = true;

        if (this.accountsTabInit) {
            dep = ["ACCOUNTS"];
            sessionOverridesDefault = false;
        } else if (this.restrictionsTabInit) {
            dep = ["SECURITY_RESTRICTIONS"];
            sessionOverridesDefault = false;
        } else if (this.gainsTabInit) {
            dep = ["GAINS_MANAGEMENT"];
            sessionOverridesDefault = false;
        } else if (this.notesTabInit) {
            dep = ["PORTFOLIO_NOTES"];
            sessionOverridesDefault = false;
        } else if (this.scheduledRebalancesTabInit) {
            dep = ["SCHEDULED_REBALANCES_LIST"];
            sessionOverridesDefault = false;
        } else if (this.automatedRebalanceInit) {
            dep = ["AUTOMATED_REBALANCING"];
            sessionOverridesDefault = false;
        } else if (this.equivalentsAlternativesTabInit) {
            dep = ["EQUIVALENTS_ALTERNATIVES"];
            sessionOverridesDefault = false;
        }

        if (!this.isPortfEqvEnabled) {
            const index = dep.indexOf('EQUIVALENTS_ALTERNATIVES');
            if (index > -1) {
                dep.splice(index, 1);
            }
        }

        this.localEditorsService.loadExpansionState(dep.length, "editor-portfolio", dep, sessionOverridesDefault);
    }

    save() {
        this.ats.trackClickEvent("lc_editsave");

        this.validateInput().subscribe(errors => {
            if (errors.length > 0) {
                this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                    dType: 'ERROR',
                    dTitle: 'Alert',
                    dProceedBtnLabel: 'Close',
                    msg: errors.join('\n'),
                    onProceed: () => {
                    }
                } as IAlertDialogData);
                return;
            } else {
                const saveReqData = this.prepareRequestForSave();
                this.as.fireEvent(IrbEditorsEvents.SAVE_START, {"slot": this.slot});

                this.ies.savePortfolioDetails(saveReqData).subscribe((data: any) => {
                    const statusId=data?.responseUtilityMap?.statusId;
                    if (statusId) {
                        this.timeout = 0;
                        this.updateStatusCount=0;
                        this.savePortfolioDetailCheckIntervalStart(statusId, saveReqData);
                    } else {
                        this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
                    }
                }, (err: HttpErrorResponse) => {
                    this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
                    let m = err.message ? err.message : 'Editor Portfolio save failed.';
                    m += ' portfolioId=' + saveReqData.portfolioId;
                    if (!this.isUiTest)
                        throw new Error(m);
                });
            }
           
        });
        
    }

    showStaleDataDialog() {
        this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
            dTitle: 'Refresh Required',
            dProceedBtnLabel: 'Refresh',
            msg: "You are attempting to edit a portfolio that was just modified by someone else. To see the updated portfolio, you need to refresh the page.",
            onProceed: () => {this.as.fireEvent(IrbAppEvents.GRID_REFRESH)}
        } as IAlertDialogData);
    }
    savePortfolioDetailCheckIntervalStart(statusId: any, saveReqData: any) {
        this.ies.savePortfolioDetailsStatus(statusId).subscribe((statusResponse: any) => {
            if (statusResponse.responseStatus === "SUCCESS") {
                const response = statusResponse.responseData;
                const reqStatus = response?.reqStatus?.trim().toUpperCase();
                if (reqStatus === 'FAILED') {
                    if (response.failMsg && response.failMsg.toUpperCase() === 'STALEDATAEXCEPTION') {
                        this.showStaleDataDialog();
                    }
                    else {
                        this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
                    }
                } else if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
                    this.timeout=(this.updateStatusCount > 10) ? 8000 : (this.updateStatusCount > 2) ? 3000 : 1500;
                    if (this.isUiTest)
                        this.timeout = 0;
                    setTimeout(() => this.savePortfolioDetailCheckIntervalStart(statusId, saveReqData),this.timeout);
                    this.updateStatusCount++;
                } else if (reqStatus === 'COMPLETED') {
                    this.actionStatusComplete(statusResponse, saveReqData);
                    this.updateStatusCount=0;
                } else {
                    this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
                }
            } else {
                this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
            }
        }, () => {
            this.close();
            this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
        });
    }
    actionStatusComplete(data: any, saveReqData: any) {
                    const response = data as IPortfolioDetailSaveResponse;

                    if (response.responseStatus === "SUCCESS") {
                        if (response.responseUtilityMap != null) {
                            //TODO: Show validation warning
                            // console.log("response.responseUtilityMap : " + response.responseUtilityMap);
                        } else {

                            // if (response.responseData != null) {
                            //     this.ies.getPortfolioDetails(saveReqData.portfolioId).subscribe((data: IPortfolioDetail) => {
                            //
                            //         this.as.fireEvent(IrbEditorsEvents.PF_RESTRICTIONS_REFRESH, {
                            //             "data": data.portfolioRestrictions,
                            //             "slot": this.slot
                            //         });
                            //
                            //         this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
                            //             "hasPendingFunction": this.hasPendingFunction,
                            //             "data": "Portfolio updated.",
                            //             "slot": this.slot
                            //         });
                            //        // this.load(data);
                            //     });
                            // }
                            if (!this.reloadAfterModelClosed) {
                              this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
                            }
                            this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
                            const portfolioId = this.portfolioId;
                            setTimeout(() => {
                                this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
                                    "hasPendingFunction": this.hasPendingFunction,
                                    "data": "Portfolio updated.",
                                    "slot": this.slot
                                });
                                this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, {"slot": this.slot});
                            }, 500);

                        }
                    } else if (response.responseStatus === 'STALEDATAEXCEPTION') {
                            this.showStaleDataDialog();
                    } else {
                        this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
                        if (!this.isUiTest)
                            throw new Error('Editor Portfolio save failed. portfolioId=' + saveReqData.portfolioId);
                    }
    }
    closeAndReopen(portfolioId: any) {
      this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot})
      if (this.reloadAfterModelClosed) {
        this.reloadAfterModelClosed = false;
        this.as.fireEvent(IrbClientsEvents.SHOW_EDIT_PORTFOLIO, {
            data: {portfolioId: portfolioId}
        });
      }
    }
    processEvent(event: IEvent) {
        super.processEvent(event);

        switch (event.type) {
            case IrbEditorsEvents.ASSIGN_ACCOUNTS:
                this.assignAccountsToPortfolio(event.data as Array<IAccount>);
                break;
            case IrbEditorsEvents.SHOW_ACCOUNT_EDIT:
                this.checkForModifications(() => {
                    this.close();
                    this.as.fireEvent(IrbClientsEvents.SHOW_EDIT_ACCOUNT, {data: event.data, "slot": this.slot});
                });
                break;
        }

        this.cdr.detectChanges();
    }

    assignAccountsToPortfolio(pfAccounts: Array<IAccount>) {
        if (pfAccounts != null && pfAccounts.length > 0) {
            pfAccounts.forEach((acct) => {
                acct.isNew = true;
                acct.masterAcctNumber = acct.masterAccount;
                acct.accountLocationType = acct.taxability
                this.data.accountsSet.push(acct);
            });
        }

        this.onInputChange();
        this.assignedAccounts.load();
    }

    saveInlineEdit(portfolioName: any) {
        // console.log(portfolioName);
        if (portfolioName.trim() === '') {
            this.closeInlineEdit();
            return;
        }
        this.ies.validatePortfolioName(portfolioName).subscribe((data: any) => {
            // console.log(data);

            if (!data.pfExists) {
                this.tooltipMessage = "";
                this.data.portfolioName = portfolioName;
                this.showInLineeditFlag = false;
                this.onInputChange();
            } else {
                this.tooltipMessage = "This portfolio name already exists.  Please enter a different name.";
            }
        });


    }

    onTextChange() {
        this.tooltipMessage = "";
    }

    showInlineEdit() {
        this.showInLineeditFlag = true;
        this.cdr.detectChanges();
    }

    closeInlineEdit() {
        this.tooltipMessage = "";
        this.data.portfolioName = super.getOriginalData().portfolioName;
        this.onInputChange();
        this.showInLineeditFlag = false;
        this.cdr.detectChanges();
    }

    get assignedAccounts() {
        return this._assignedAccounts;
    }

    get collapseForOpenTriggers() {
        return this._collapseForOpenTriggers;
    }

    @HostListener("window:resize")
    onResize() {
        if (this.body) {
            const rect = this.body.nativeElement.getBoundingClientRect();
            let h = (window.innerHeight - rect.top - 10);

            h = (h < 200) ? 200 : h;
            this.bodyHeight = h + "px";
        }
    }

    get isUnAuthorized() {
        return this._isUnAuthorized;
    }

    set isUnAuthorized(isUnAuthorized: boolean) {
        this._isUnAuthorized = isUnAuthorized;
    }

    setPortfolioAuthorization() {
        if (this.data && this.data.authStatus && this.data.authStatus === 'Unauthorized') {
            this.isUnAuthorized = true;
        } else {
            this.isUnAuthorized = false;
        }
    }

    //function to exclude attributes that needn't be sent for save/update
    prepareRequestForSave(){
        let saveDataReq  = JSON.parse(JSON.stringify(this.data));
        if (saveDataReq && saveDataReq.createdDate) {
            delete saveDataReq.createdDate;
        }

        return saveDataReq;
    }

    validatePortfolioName(s: string):boolean {
        return (new RegExp("^[a-zA-Z0-9.]([a-zA-Z0-9,. +=@-]?)+$")).test(s);
    }
    
    validateInput():Observable<string[]>  {
        const errors: string[] = [];

        if (this.data?.isAutoRebalEnabled && this.data?.autoScheduler?.recurringFreqType === 'Weekly') {
            if (!this.data.autoScheduler.monday && !this.data.autoScheduler.tuesday && !this.data.autoScheduler.wednesday && !this.data.autoScheduler.thursday && !this.data.autoScheduler.friday && !this.data.autoScheduler.saturday && !this.data.autoScheduler.sunday) {
                errors.push('Please select day(s) of the week for the automated rebalance');
            }
        }

        let isActCashUpfront = false;

        if (this.data?.accountsSet && this.data?.accountsSet?.length) {
          for (let i = 0; i < this.data.accountsSet.length; i++) {
            let acc = this.data.accountsSet[i];
            if (acc.actBlockAutoSchd) {
              isActCashUpfront = true;
              break;
            }
          }
        }

        if (isActCashUpfront && (this.data?.autoRebalType?.toLowerCase() === 'full rebalance' || this.data?.autoRebalType?.toLowerCase() === 'tax loss harvest and full rebalance')) {
          errors.push('This Portfolio contains account with Cash Up Front or Settled Cash Up Front restriction. Only Cash management rebalance types can be automated.');
        }

        if(!this.isGainsManagementValid) {
            errors.push('Gains Threshold Amount cannot be empty or invalid.');
        }

        if(!this.isSecurityRestrictionsValid) {
            errors.push('Alternate Security cannot be empty.');
        }

        return of(errors);
    }

    onSelectedModelChange(model: IModelDetail) {
      this.selectedModel = model;
    }

    viewPortfolioModel() {
      let data: any =  {};
      data.slot ="B";
      data.isDraft = false;
      data.parentObj = this;
      if(this.selectedModel && this.selectedModel.modelType ==="Blended") {
        let modelId = this.selectedModel.securityModelId.replace('b','').replace('B','');
        data.blendedSecurityModelId = modelId;
        data.modelId = modelId;
        data.type = this.selectedModel.modelType;
      } else {
        data.modelId = this.selectedModel.securityModelId;
        data.type = "Base";
      }
      if (!this.isModified) {
        this.as.fireEvent(IrbModelsEvents.SHOW_EDIT_MODEL, data);
      } else {
        this.reloadAfterModelClosed = true;
        this.checkForModifications(() => {
          this.as.fireEvent(IrbModelsEvents.SHOW_EDIT_MODEL, data);
          if (this.portfolioId) {
            //this.closeAndReopen(this.portfolioId);
            this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
          }
        });
      }
    }
protected readonly faChevronRight = faChevronRight; }
